import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import { 
    Shield, 
    Users, 
    Save,
    RefreshCw,
    Search,
    Check,
    X,
    Eye,
    Edit,
    Settings,
    LayoutDashboard,
    Package,
    ShoppingCart,
    DollarSign,
    ChefHat,
    Wine,
    UserCog,
    Building2,
    FileText,
    AlertTriangle,
    Info
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ROLE_PERMISSIONS, UNIFIED_MENU_CONFIG } from '../config/menuConfig';

// ============================================================
// GESTION DES PERMISSIONS - ADMIN UNIQUEMENT
// Interface visuelle pour modifier les permissions par rôle
// ============================================================

// Permission categories for better organization
const PERMISSION_CATEGORIES = {
    dashboard: {
        label: 'Tableaux de bord',
        icon: LayoutDashboard,
        color: 'text-blue-500',
        permissions: ['canViewDashboard', 'canViewAdminDashboard', 'canViewLiveMonitoring']
    },
    stock: {
        label: 'Stock & Inventaire',
        icon: Package,
        color: 'text-amber-500',
        permissions: ['canViewStock', 'canManageStock', 'canManageRecipes']
    },
    sales: {
        label: 'Ventes & POS',
        icon: ShoppingCart,
        color: 'text-green-500',
        permissions: ['canViewSales', 'canManageSales', 'canUsePOS', 'canManageCash']
    },
    finance: {
        label: 'Finance & Rapports',
        icon: DollarSign,
        color: 'text-emerald-500',
        permissions: ['canViewFinance', 'canManageFinance', 'canViewReports', 'canViewAudit']
    },
    kitchen: {
        label: 'Cuisine & Bar',
        icon: ChefHat,
        color: 'text-orange-500',
        permissions: ['canViewKitchen', 'canViewBar']
    },
    personnel: {
        label: 'Personnel & RH',
        icon: Users,
        color: 'text-purple-500',
        permissions: ['canViewPersonnel', 'canManagePersonnel', 'canClock', 'canViewOwnStats']
    },
    admin: {
        label: 'Administration',
        icon: Settings,
        color: 'text-red-500',
        permissions: ['canViewSettings', 'canManageSettings', 'canManageEstablishments', 'canViewCredentials']
    }
};

// Permission labels in French
const PERMISSION_LABELS = {
    canViewDashboard: { label: 'Voir Dashboard', description: 'Accès au tableau de bord principal' },
    canViewAdminDashboard: { label: 'Dashboard Admin', description: 'Accès au dashboard administrateur' },
    canViewLiveMonitoring: { label: 'Supervision Live', description: 'Voir la supervision en temps réel' },
    canViewStock: { label: 'Voir Stock', description: 'Consulter l\'inventaire' },
    canManageStock: { label: 'Gérer Stock', description: 'Modifier les stocks, entrées/sorties' },
    canManageRecipes: { label: 'Gérer Recettes', description: 'Créer et modifier les recettes' },
    canViewSales: { label: 'Voir Ventes', description: 'Consulter les ventes' },
    canManageSales: { label: 'Gérer Ventes', description: 'Gérer les produits, prix, menus' },
    canUsePOS: { label: 'Utiliser POS', description: 'Accès au point de vente' },
    canManageCash: { label: 'Gérer Caisse', description: 'Ouvrir/fermer la caisse' },
    canViewFinance: { label: 'Voir Finance', description: 'Consulter les données financières' },
    canManageFinance: { label: 'Gérer Finance', description: 'Modifier charges, décaissements' },
    canViewReports: { label: 'Voir Rapports', description: 'Accéder aux rapports' },
    canViewAudit: { label: 'Journal Audit', description: 'Voir le journal d\'audit' },
    canViewKitchen: { label: 'Voir Cuisine', description: 'Accès à l\'écran cuisine' },
    canViewBar: { label: 'Voir Bar', description: 'Accès à l\'écran bar' },
    canViewPersonnel: { label: 'Voir Personnel', description: 'Consulter les employés' },
    canManagePersonnel: { label: 'Gérer Personnel', description: 'Modifier les employés, planning' },
    canClock: { label: 'Pointer', description: 'Peut pointer son arrivée/départ' },
    canViewOwnStats: { label: 'Mes Statistiques', description: 'Voir ses propres stats' },
    canViewSettings: { label: 'Voir Paramètres', description: 'Accéder aux paramètres' },
    canManageSettings: { label: 'Gérer Paramètres', description: 'Modifier les paramètres' },
    canManageEstablishments: { label: 'Établissements', description: 'Gérer les établissements' },
    canViewCredentials: { label: 'Identifiants', description: 'Voir les identifiants système' }
};

// Role labels and colors
const ROLE_CONFIG = {
    ADMIN: { label: 'Administrateur', color: 'bg-red-100 text-red-800', icon: Shield },
    DIRECTEUR: { label: 'Directeur', color: 'bg-purple-100 text-purple-800', icon: Building2 },
    DIRECTEUR_ADJOINT: { label: 'Directeur Adjoint', color: 'bg-indigo-100 text-indigo-800', icon: UserCog },
    MAITRE_HOTEL: { label: "Maître d'Hôtel", color: 'bg-blue-100 text-blue-800', icon: Users },
    SERVEUR: { label: 'Serveur', color: 'bg-green-100 text-green-800', icon: Users },
    SERVEUSE: { label: 'Serveuse', color: 'bg-emerald-100 text-emerald-800', icon: Users },
    CAISSIER: { label: 'Caissier', color: 'bg-amber-100 text-amber-800', icon: DollarSign },
    CHEF_CUISINE: { label: 'Chef Cuisine', color: 'bg-orange-100 text-orange-800', icon: ChefHat },
    CUISINIER: { label: 'Cuisinier', color: 'bg-orange-50 text-orange-700', icon: ChefHat },
    BARMAN: { label: 'Barman', color: 'bg-indigo-50 text-indigo-700', icon: Wine },
    STOCK_MANAGER: { label: 'Resp. Stock', color: 'bg-amber-50 text-amber-700', icon: Package },
    EMPLOYE: { label: 'Employé', color: 'bg-gray-100 text-gray-800', icon: Users }
};

const PermissionsManagementPage = () => {
    const { api, user } = useAuth();
    
    // State
    const [permissions, setPermissions] = useState({ ...ROLE_PERMISSIONS });
    const [originalPermissions, setOriginalPermissions] = useState({ ...ROLE_PERMISSIONS });
    const [selectedRole, setSelectedRole] = useState('DIRECTEUR');
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    
    // Check admin access
    const isAdmin = user?.role === 'ADMIN';
    
    // Load permissions from backend (if stored there)
    const loadPermissions = useCallback(async () => {
        try {
            const response = await api().get('/admin/permissions');
            if (response.data?.permissions) {
                setPermissions(response.data.permissions);
                setOriginalPermissions(response.data.permissions);
            }
        } catch (error) {
            // Use default permissions if no custom ones saved
            console.log('Using default permissions');
        }
    }, [api]);
    
    useEffect(() => {
        if (isAdmin) {
            loadPermissions();
        }
    }, [isAdmin, loadPermissions]);
    
    // Toggle a permission
    const togglePermission = (role, permission) => {
        if (role === 'ADMIN') {
            toast.error('Les permissions Admin ne peuvent pas être modifiées');
            return;
        }
        
        setPermissions(prev => {
            const newPerms = { ...prev };
            newPerms[role] = { ...newPerms[role], [permission]: !newPerms[role][permission] };
            return newPerms;
        });
        setHasChanges(true);
    };
    
    // Save permissions
    const savePermissions = async () => {
        try {
            setSaving(true);
            await api().put('/admin/permissions', { permissions });
            setOriginalPermissions({ ...permissions });
            setHasChanges(false);
            toast.success('Permissions enregistrées avec succès');
            setShowConfirmDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };
    
    // Reset to original
    const resetPermissions = () => {
        setPermissions({ ...originalPermissions });
        setHasChanges(false);
        toast.info('Modifications annulées');
    };
    
    // Count permissions for a role
    const countPermissions = (role) => {
        const rolePerms = permissions[role] || {};
        return Object.values(rolePerms).filter(Boolean).length;
    };
    
    // Filter permissions by search
    const filterPermissions = (perms) => {
        if (!searchTerm) return perms;
        return perms.filter(p => 
            PERMISSION_LABELS[p]?.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            PERMISSION_LABELS[p]?.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };
    
    if (!isAdmin) {
        return (
            <div className="flex items-center justify-center h-96 p-4">
                <Card className="p-6 sm:p-8 text-center max-w-sm">
                    <Shield className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-red-500 mb-4" />
                    <h2 className="text-lg sm:text-xl font-bold text-red-600">Accès Refusé</h2>
                    <p className="text-sm text-muted-foreground mt-2">
                        Cette page est réservée à l'administrateur.
                    </p>
                </Card>
            </div>
        );
    }
    
    const rolePerms = permissions[selectedRole] || {};
    
    return (
        <div className="space-y-4 p-3 sm:p-4 md:p-6" data-testid="permissions-management-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                        <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                        Gestion des Permissions
                    </h1>
                    <p className="text-xs text-muted-foreground">
                        Configurez les accès pour chaque rôle
                    </p>
                </div>
                
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    {hasChanges && (
                        <>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={resetPermissions}
                                className="flex-1 sm:flex-none h-8 text-xs"
                            >
                                <X className="w-3 h-3 mr-1" />
                                Annuler
                            </Button>
                            <Button 
                                size="sm"
                                onClick={() => setShowConfirmDialog(true)}
                                className="flex-1 sm:flex-none h-8 text-xs bg-green-600 hover:bg-green-700"
                            >
                                <Save className="w-3 h-3 mr-1" />
                                Enregistrer
                            </Button>
                        </>
                    )}
                </div>
            </div>
            
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-800">
                        Les modifications seront appliquées immédiatement après enregistrement. 
                        Les utilisateurs devront se reconnecter pour voir les changements.
                    </p>
                </div>
            </div>
            
            {/* Main Content - Responsive Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Roles List - Sidebar on large screens, tabs on mobile */}
                <Card className="lg:col-span-1">
                    <CardHeader className="pb-2 px-3 pt-3">
                        <CardTitle className="text-sm">Rôles</CardTitle>
                    </CardHeader>
                    <CardContent className="p-2">
                        {/* Mobile: Horizontal scroll */}
                        <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                            {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                                const RoleIcon = config.icon;
                                const permCount = countPermissions(role);
                                const isSelected = selectedRole === role;
                                const isAdminRole = role === 'ADMIN';
                                
                                return (
                                    <button
                                        key={role}
                                        onClick={() => setSelectedRole(role)}
                                        disabled={isAdminRole}
                                        className={cn(
                                            "flex items-center gap-2 p-2 rounded-lg transition-all min-w-[140px] lg:min-w-0 lg:w-full",
                                            isSelected ? "bg-primary text-white" : "hover:bg-muted",
                                            isAdminRole && "opacity-50 cursor-not-allowed"
                                        )}
                                    >
                                        <RoleIcon className="w-4 h-4 flex-shrink-0" />
                                        <div className="flex-1 text-left">
                                            <p className="text-xs font-medium truncate">{config.label}</p>
                                            <p className={cn(
                                                "text-[10px]",
                                                isSelected ? "text-white/70" : "text-muted-foreground"
                                            )}>
                                                {permCount} permissions
                                            </p>
                                        </div>
                                        {isAdminRole && (
                                            <Badge variant="outline" className="text-[8px] px-1">
                                                Verrouillé
                                            </Badge>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
                
                {/* Permissions Grid */}
                <Card className="lg:col-span-3">
                    <CardHeader className="pb-2 px-3 pt-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                                <CardTitle className="text-sm flex items-center gap-2">
                                    Permissions: 
                                    <Badge className={cn("text-xs", ROLE_CONFIG[selectedRole]?.color)}>
                                        {ROLE_CONFIG[selectedRole]?.label}
                                    </Badge>
                                </CardTitle>
                                <CardDescription className="text-xs">
                                    {countPermissions(selectedRole)} permissions actives
                                </CardDescription>
                            </div>
                            <div className="relative w-full sm:w-48">
                                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="h-8 text-xs pl-7"
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-3">
                        <div className="space-y-4">
                            {Object.entries(PERMISSION_CATEGORIES).map(([catKey, category]) => {
                                const CategoryIcon = category.icon;
                                const filteredPerms = filterPermissions(category.permissions);
                                
                                if (filteredPerms.length === 0) return null;
                                
                                return (
                                    <div key={catKey} className="space-y-2">
                                        <div className="flex items-center gap-2 pb-1 border-b">
                                            <CategoryIcon className={cn("w-4 h-4", category.color)} />
                                            <span className="text-xs font-medium">{category.label}</span>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {filteredPerms.map(perm => {
                                                const permInfo = PERMISSION_LABELS[perm] || { label: perm, description: '' };
                                                const isEnabled = rolePerms[perm] === true;
                                                
                                                return (
                                                    <div 
                                                        key={perm}
                                                        className={cn(
                                                            "flex items-center justify-between p-2 rounded-lg border transition-all",
                                                            isEnabled ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
                                                        )}
                                                    >
                                                        <div className="flex-1 min-w-0 pr-2">
                                                            <p className="text-xs font-medium truncate">{permInfo.label}</p>
                                                            <p className="text-[10px] text-muted-foreground truncate">
                                                                {permInfo.description}
                                                            </p>
                                                        </div>
                                                        <Switch
                                                            checked={isEnabled}
                                                            onCheckedChange={() => togglePermission(selectedRole, perm)}
                                                            disabled={selectedRole === 'ADMIN'}
                                                            className="flex-shrink-0"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Quick Actions - Responsive */}
            <Card>
                <CardHeader className="pb-2 px-3 pt-3">
                    <CardTitle className="text-sm">Actions rapides</CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-auto py-2 text-xs"
                            onClick={() => {
                                const newPerms = { ...permissions };
                                Object.keys(PERMISSION_LABELS).forEach(p => {
                                    if (newPerms[selectedRole]) {
                                        newPerms[selectedRole][p] = true;
                                    }
                                });
                                setPermissions(newPerms);
                                setHasChanges(true);
                            }}
                            disabled={selectedRole === 'ADMIN'}
                        >
                            <Check className="w-3 h-3 mr-1" />
                            Tout activer
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-auto py-2 text-xs"
                            onClick={() => {
                                const newPerms = { ...permissions };
                                Object.keys(PERMISSION_LABELS).forEach(p => {
                                    if (newPerms[selectedRole]) {
                                        newPerms[selectedRole][p] = false;
                                    }
                                });
                                setPermissions(newPerms);
                                setHasChanges(true);
                            }}
                            disabled={selectedRole === 'ADMIN'}
                        >
                            <X className="w-3 h-3 mr-1" />
                            Tout désactiver
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-auto py-2 text-xs"
                            onClick={() => {
                                // Copy permissions from another role
                                const source = selectedRole === 'DIRECTEUR' ? 'DIRECTEUR_ADJOINT' : 'DIRECTEUR';
                                setPermissions(prev => ({
                                    ...prev,
                                    [selectedRole]: { ...prev[source] }
                                }));
                                setHasChanges(true);
                                toast.info(`Permissions copiées depuis ${ROLE_CONFIG[source].label}`);
                            }}
                            disabled={selectedRole === 'ADMIN'}
                        >
                            <Eye className="w-3 h-3 mr-1" />
                            Copier de...
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-auto py-2 text-xs"
                            onClick={() => {
                                setPermissions(prev => ({
                                    ...prev,
                                    [selectedRole]: { ...ROLE_PERMISSIONS[selectedRole] }
                                }));
                                setHasChanges(true);
                                toast.info('Permissions par défaut restaurées');
                            }}
                            disabled={selectedRole === 'ADMIN'}
                        >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Par défaut
                        </Button>
                    </div>
                </CardContent>
            </Card>
            
            {/* Confirm Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent className="max-w-sm mx-4">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Confirmer les modifications
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            Les nouvelles permissions seront appliquées immédiatement. 
                            Les utilisateurs concernés devront se reconnecter.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setShowConfirmDialog(false)}
                            className="w-full sm:w-auto"
                            size="sm"
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={savePermissions}
                            disabled={saving}
                            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                            size="sm"
                        >
                            {saving ? (
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                                <Save className="w-3 h-3 mr-1" />
                            )}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PermissionsManagementPage;
